import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Check, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const PaquetesPrecios = () => {
  const paquetesConsultoria = [
    {
      name: "Claridad Financiera",
      description: "Ideal para empresas que necesitan entender su situación financiera actual",
      features: [
        "Diagnóstico financiero integral",
        "Análisis de estados financieros",
        "Identificación de riesgos y fugas de rentabilidad",
        "Análisis básico de flujo de caja",
        "Recomendaciones estratégicas",
        "Informe ejecutivo"
      ],
      recommended: false
    },
    {
      name: "Control y Rentabilidad",
      description: "Para empresas que buscan implementar controles financieros efectivos",
      features: [
        "Todo lo incluido en Claridad Financiera",
        "Implementación de Profit First",
        "Flujo de caja proyectado",
        "Análisis de pricing y márgenes",
        "Presupuesto y seguimiento mensual"
      ],
      recommended: true
    },
    {
      name: "Dirección Financiera Estratégica",
      description: "Dirección financiera completa para empresas en crecimiento acelerado",
      features: [
        "Todo lo incluido en Control y Rentabilidad",
        "Estrategia de crecimiento financiero",
        "Modelos de negocio sostenibles",
        "Acompañamiento a gerencia",
        "Participación en junta directiva o comité financiero",
        "Mentoring ejecutivo"
      ],
      recommended: false
    }
  ];

  const paquetesContables = [
    {
      name: "Orden Contable",
      description: "Servicio básico para mantener la contabilidad al día",
      features: [
        "Registro contable básico",
        "Hasta 100 documentos mensuales",
        "Clasificación y validación contable",
        "Reporte simplificado"
      ],
      recommended: false
    },
    {
      name: "Gestión Administrativa Contable",
      description: "Gestión contable completa con nómina y facturación",
      features: [
        "Contabilidad completa",
        "Hasta 250 documentos mensuales",
        "Facturación electrónica",
        "Nómina hasta 5 empleados",
        "Seguridad social",
        "Impuestos nacionales básicos",
        "Reportes mensuales"
      ],
      recommended: true
    },
    {
      name: "Backoffice Empresarial",
      description: "Operación contable y administrativa completa",
      features: [
        "Contabilidad NIIF para PYMEs",
        "Hasta 300 documentos mensuales",
        "Facturación electrónica",
        "Nómina hasta 15 empleados",
        "Tesorería básica",
        "Gestión de cartera y proveedores",
        "Impuestos nacionales y territoriales",
        "Reportes financieros"
      ],
      recommended: false
    }
  ];

  const paquetesIntegrados = [
    {
      name: "Crecimiento con Control",
      description: "Gestión Administrativa Contable + Claridad Financiera",
      features: [
        "Contabilidad completa y diagnóstico financiero",
        "Hasta 250 documentos mensuales",
        "Facturación y nómina (hasta 5 empleados)",
        "Análisis financiero integral"
      ],
      recommended: false
    },
    {
      name: "Escalamiento Financiero",
      description: "Backoffice Empresarial + Control y Rentabilidad",
      features: [
        "Operación contable completa",
        "Hasta 300 documentos mensuales",
        "Nómina hasta 15 empleados",
        "Implementación de Profit First",
        "Control financiero estratégico"
      ],
      recommended: true
    },
    {
      name: "Dirección y Expansión",
      description: "Backoffice Empresarial + Dirección Financiera Estratégica",
      features: [
        "Operación contable y administrativa completa",
        "CFO estratégico dedicado",
        "Estrategia de crecimiento y expansión",
        "Mentoring ejecutivo",
        "Participación en comité financiero"
      ],
      recommended: false
    }
  ];

  const PriceCard = ({ paquete, category }) => (
    <div className={`group bg-white rounded-lg border-2 ${paquete.recommended ? 'border-sinergia-azul shadow-xl' : 'border-gray-200'} overflow-hidden relative hover:shadow-xl transition-all duration-300 hover:-translate-y-1`}>
      {paquete.recommended && (
        <div className="bg-sinergia-azul text-white text-center py-2 text-sm font-semibold">
          Más Popular
        </div>
      )}
      <div className="p-6">
        <h3 className="heading-4 text-2xl font-bold mb-2 text-sinergia-gris-oscuro">{paquete.name}</h3>
        <p className="body-small text-gray-600 mb-6">{paquete.description}</p>
        
        <ul className="space-y-3 mb-6">
          {paquete.features.map((feature, index) => (
            <li key={index} className="flex items-start">
              <Check className="w-5 h-5 text-sinergia-verde-claro mr-2 flex-shrink-0 mt-0.5" />
              <span className="body-small text-gray-700">{feature}</span>
            </li>
          ))}
        </ul>

        <Link to="/contacto" state={{ selectedService: paquete.name }}>
          <Button 
            className={`w-full ${paquete.recommended ? 'btn-primary' : 'btn-secondary'}`}
          >
            Solicitar este paquete
            <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        {/* Meta Tags Básicos */}
        <title>Paquetes y Precios | CFO Externo y Servicios Contables | Sinergia Empresarial CSCE</title>
        <meta name="description" content="Conoce nuestros paquetes de consultoría financiera, CFO externo y servicios contables para PYMEs. Planes flexibles desde Claridad Financiera hasta Dirección Estratégica." />
        <meta name="keywords" content="precios CFO externo, paquetes consultoría financiera, planes servicios contables, tarifas contador público, consultoría financiera precios" />
        <link rel="canonical" href="https://www.sinergiaempresarialcsce.com/paquetes-precios" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.sinergiaempresarialcsce.com/paquetes-precios" />
        <meta property="og:title" content="Paquetes y Precios | Sinergia Empresarial CSCE" />
        <meta property="og:description" content="Planes flexibles de consultoría financiera y servicios contables para PYMEs en Colombia." />
        <meta property="og:image" content="https://www.sinergiaempresarialcsce.com/logotipo_sinergia.png" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://www.sinergiaempresarialcsce.com/paquetes-precios" />
        <meta name="twitter:title" content="Paquetes y Precios | Sinergia Empresarial CSCE" />
        <meta name="twitter:description" content="Planes flexibles de consultoría financiera y servicios contables para PYMEs." />
        <meta name="twitter:image" content="https://www.sinergiaempresarialcsce.com/logotipo_sinergia.png" />
        
        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            "name": "Servicios de Consultoría Financiera y Contable",
            "description": "Paquetes de CFO externo, consultoría financiera y servicios contables para PYMEs",
            "provider": {
              "@type": "Organization",
              "name": "Sinergia Empresarial CSCE"
            },
            "offers": {
              "@type": "AggregateOffer",
              "priceCurrency": "COP",
              "offerCount": "3",
              "offers": [
                {
                  "@type": "Offer",
                  "name": "Claridad Financiera",
                  "description": "Diagnóstico financiero integral para empresas"
                },
                {
                  "@type": "Offer",
                  "name": "Control y Rentabilidad",
                  "description": "Implementación de controles financieros efectivos"
                },
                {
                  "@type": "Offer",
                  "name": "Dirección Financiera Estratégica",
                  "description": "Dirección financiera completa para empresas en crecimiento"
                }
              ]
            }
          })}
        </script>
      </Helmet>

      {/* Header Section */}
      <section className="bg-gradient-to-r from-sinergia-azul to-sinergia-verde text-white py-16">
        <div className="container mx-auto px-6">
          <h1 className="heading-1 text-5xl font-bold mb-4">
            Paquetes
          </h1>
          <p className="body-large max-w-3xl">
            Elige el paquete que mejor se adapte a las necesidades de tu empresa. Todos incluyen acompañamiento personalizado.
          </p>
        </div>
      </section>

      {/* Paquetes de Consultoría */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <h2 className="heading-2 text-4xl font-bold mb-4 text-center text-sinergia-gris-oscuro">
            Paquetes de Consultoría Financiera
          </h2>
          <p className="body-medium text-gray-600 text-center mb-12 max-w-3xl mx-auto">
            Dirección financiera estratégica para empresas que buscan crecer con rentabilidad.
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            {paquetesConsultoria.map((paquete, index) => (
              <PriceCard key={index} paquete={paquete} category="consultoria" />
            ))}
          </div>
        </div>
      </section>

      {/* Paquetes Contables */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <h2 className="heading-2 text-4xl font-bold mb-4 text-center text-sinergia-gris-oscuro">
            Paquetes Contables
          </h2>
          <p className="body-medium text-gray-600 text-center mb-12 max-w-3xl mx-auto">
            Operación contable y administrativa para que te enfoques en tu negocio.
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            {paquetesContables.map((paquete, index) => (
              <PriceCard key={index} paquete={paquete} category="contable" />
            ))}
          </div>
        </div>
      </section>

      {/* Paquetes Integrados */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <h2 className="heading-2 text-4xl font-bold mb-4 text-center text-sinergia-gris-oscuro">
            Paquetes Integrados
          </h2>
          <p className="body-medium text-gray-600 text-center mb-12 max-w-3xl mx-auto">
            Soluciones completas que combinan consultoría estratégica y operación contable.
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            {paquetesIntegrados.map((paquete, index) => (
              <PriceCard key={index} paquete={paquete} category="integrado" />
            ))}
          </div>
        </div>
      </section>

      {/* Note Section */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="bg-white rounded-lg p-8 border border-sinergia-gris-claro">
            <h3 className="heading-4 text-xl font-semibold mb-4 text-sinergia-gris-oscuro">Soluciones a medida para tu empresa</h3>
            <p className="body-medium text-gray-700">
              Cada empresa es única. Por eso personalizamos nuestros servicios para adaptarlos perfectamente a tus necesidades específicas. Nuestro equipo trabaja contigo para diseñar la solución ideal que acelere tu crecimiento y maximice tu rentabilidad.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-sinergia-azul text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="heading-2 text-4xl font-bold mb-4">
            ¿Necesitas asesoría para elegir?
          </h2>
          <p className="body-large mb-8 max-w-2xl mx-auto">
            Contáctanos y te ayudaremos a identificar el paquete ideal para tu empresa.
          </p>
          <Link to="/contacto">
            <Button className="bg-white text-sinergia-azul hover:bg-gray-100 px-8 py-4 text-lg font-semibold rounded-full">
              Solicita recomendación personalizada
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default PaquetesPrecios;
